import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { DollarSign, ShoppingBag, TrendingUp, Receipt, Download } from "lucide-react";
import { DateRangePicker } from "@/components/admin/DateRangePicker";
import { ResetSalesButton } from "@/components/admin/ResetSalesButton";
import { useAuth } from "@/context/AuthContext";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

export default function StoreSales() {
  const { sid } = useParams();
  const { user } = useAuth();
  const [range, setRange] = useState("today");
  const [custom, setCustom] = useState(null); // { start, end } ISO date strings
  const [data, setData] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const qs = range === "custom" && custom
      ? `range=custom&start=${custom.start}&end=${custom.end}`
      : `range=${range}`;
    api.get(`/stores/${sid}/sales?${qs}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [sid, range, custom, reloadKey]);

  const applyCustom = ({ start, end }) => { setCustom({ start, end }); setRange("custom"); };

  const downloadCsv = () => {
    const qs = range === "custom" && custom
      ? `range=custom&start=${custom.start}&end=${custom.end}`
      : `range=${range}`;
    const url = `${api.defaults.baseURL}/stores/${sid}/sales.csv?${qs}`;
    window.open(url, "_blank");
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">Store · Website Orders</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="sales-title">
            Online Sales &amp; Reports
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 h-8 rounded-full text-xs font-medium ${range === r.key ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"}`}
                data-testid={`sales-range-${r.key}`}
              >{r.label}</button>
            ))}
          </div>
          <DateRangePicker
            value={range === "custom" && custom ? { from: new Date(custom.start + "T00:00:00"), to: new Date(custom.end + "T00:00:00") } : null}
            onApply={applyCustom}
          />
          <button
            type="button"
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 px-3 h-8 rounded-full text-xs font-medium border border-neutral-200 bg-white text-neutral-700 hover:text-neutral-900"
            data-testid="sales-csv-download"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <ResetSalesButton user={user} storeId={sid} scopeLabel="this store" onDone={() => setReloadKey((k) => k + 1)} />
        </div>
      </div>

      {!data ? <div className="text-neutral-500 text-sm">Loading…</div> : (
        <>
          <p
            className="mb-5 text-sm leading-relaxed text-neutral-500"
            data-testid="online-sales-scope-note"
          >
            These reports include website orders only. View in-person Fort Meade sales in Square.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi icon={DollarSign} label="Online Gross" value={`$${data.gross.toFixed(2)}`} testid="kpi-gross"/>
            <Kpi icon={Receipt} label="Online Net" value={`$${data.net.toFixed(2)}`} testid="kpi-net"/>
            <Kpi icon={ShoppingBag} label="Online Orders" value={data.order_count} testid="kpi-order-count"/>
            <Kpi icon={TrendingUp} label="Online Avg Order" value={`$${data.average_order_value.toFixed(2)}`} testid="kpi-aov"/>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <SmallKpi label="Tax" value={`$${data.tax.toFixed(2)}`}/>
            <SmallKpi label="Tips" value={`$${data.tip.toFixed(2)}`}/>
            <SmallKpi label="Discounts" value={`$${data.discount.toFixed(2)}`}/>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-neutral-200/70 p-5">
              <h2 className="font-display font-semibold mb-3">Top selling items</h2>
              {data.top_items.length === 0
                ? <p className="text-sm text-neutral-500" data-testid="top-items-empty">No sales in this range.</p>
                : <ul className="divide-y divide-neutral-100">
                    {data.top_items.map((it) => (
                      <li key={it.name} className="py-2.5 flex justify-between text-sm">
                        <span className="font-medium">{it.name}</span>
                        <span className="text-neutral-500">{it.qty} · <span className="text-neutral-900 font-semibold">${it.revenue.toFixed(2)}</span></span>
                      </li>
                    ))}
                  </ul>}
            </div>
            <div className="bg-white rounded-2xl border border-neutral-200/70 p-5">
              <h2 className="font-display font-semibold mb-3">Payment / Source breakdown</h2>
              {Object.keys(data.payment_breakdown).length === 0
                ? <p className="text-sm text-neutral-500">No transactions.</p>
                : <ul className="divide-y divide-neutral-100">
                    {Object.entries(data.payment_breakdown).map(([k, v]) => (
                      <li key={k} className="py-2.5 flex justify-between text-sm">
                        <span className="capitalize">{k}</span>
                        <span className="font-semibold">${v.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200/70 p-5" data-testid={testid}>
      <div className="w-9 h-9 rounded-xl bg-neutral-100 text-neutral-700 inline-flex items-center justify-center mb-3">
        <Icon className="w-4 h-4"/>
      </div>
      <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-1 font-display font-bold text-2xl">{value}</div>
    </div>
  );
}
function SmallKpi({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200/70 p-4">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-1 font-display font-bold text-xl">{value}</div>
    </div>
  );
}
